import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setDefaultOptions } from 'date-fns';
import { dateFnsLocale } from '#core/utils/date-locale.ts';

/** UI languages the app ships translations for. Single source of truth for language pickers. */
export const SUPPORTED_LANGUAGES = ['en', 'nl', 'fr', 'de', 'it', 'pl', 'es', 'sr', 'hr', 'uk', 'tr', 'cs', 'sk'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Where the browser remembers the last UI language chosen on it, so a public page opened
 *  without a signed-in user (a scanned QR code) can start in that language. */
export const LANGUAGE_STORAGE_KEY = 'ynteq.language';

/** The language last chosen in this browser, or null when none was chosen or storage is unavailable. */
export function readStoredLanguage(): SupportedLanguage | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(stored ?? '') ? (stored as SupportedLanguage) : null;
  } catch {
    return null;
  }
}

/** Remember `language` for this browser; a blocked storage is not worth failing a language switch over. */
export function storeLanguage(language: string): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    return;
  }
}

/** The first device language the app ships translations for, or `'en'` when there is none. */
export function detectDeviceLanguage(): SupportedLanguage {
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const candidate of candidates) {
    const primary = candidate.toLowerCase().split('-')[0];
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(primary)) return primary as SupportedLanguage;
  }
  return 'en';
}
i18n
  .use(initReactI18next)
  .use({
    type: 'backend',
    read<Namespace extends keyof unknown>(
      language: unknown,
      namespace: Namespace,
      callback: (_errorValue: unknown, _translations: null | unknown[Namespace]) => void,
    ) {
      const promisses:Promise<{name:string|undefined|null,result:object|undefined|null}>[] = [];
      if(namespace !== "translation"){
        promisses.push((async ()=>{
          // A language whose file for this namespace has not been written yet is expected,
          // not a fault — an empty object lets the English fallback supply the text, so the
          // silence here is deliberate rather than a swallowed error.
          try { return {name:namespace as string,result:await import(`./locales/${String(namespace)}/${language}.json`)} }
          catch { return {name:namespace as string,result:{default:{}}} }
        })());
        // Shared, cross-domain strings (entity property labels, common actions, importer
        // wizard, units, …) live in a single `shared` namespace and are merged into EVERY
        // namespace under the `shared` key. Any component can therefore reference
        // `t('shared.fields.name')` / `t('shared.actions.save')` without loading a second
        // namespace — the strings ride along with whatever namespace it already loaded.
        // Guarded so a missing shared file never fails the whole namespace load.
        promisses.push((async ()=>{
          try { return {name:"__shared__",result:await import(`./locales/shared/${language}.json`)} }
          catch(error){ console.error(`Failed to load shared translations for ${String(language)}`, error); return {name:"__shared__",result:{default:{}}} }
        })());
      }
      Promise.all(promisses).then(resources=>{
        let result:{[key:string]:unknown}={}
        for(const r of resources){
          if(r.name === "__shared__"){
            const mod = r.result as {default?:unknown}
            result = {...result, shared: (mod?.default ?? mod)}
          } else if(r.name!=null){
            // Read the JSON module's `default` export rather than spreading the module
            // namespace directly. Vite emits per-top-level-key named exports, but a key
            // that is a JS reserved word (e.g. `public`) gets NO named export — so
            // spreading the namespace silently drops it. The `default` export always
            // carries the complete JSON, reserved-word keys included.
            const mod = r.result as {default?:unknown}
            result = {...result, ...((mod?.default ?? mod) as object)}
          }
        }
        callback(null,result)
      }).catch(error=>{console.error(error);callback(error, null);})
    },
  })
  .init({
    lng: readStoredLanguage() ?? detectDeviceLanguage(),
    fallbackLng: 'en',
    // `shared` is preloaded as a namespace of its own so non-component modules can read a
    // shared label through i18next without waiting on a page's namespace to load.
    ns: ['translation', 'shared'],
    defaultNS: 'translation',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    returnEmptyString: false,
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  });

// Registered once init has settled so the initial language never overwrites the remembered choice;
// every later switch — the signed-in user's setting loading, a picker — is persisted.
i18n.on('initialized', () => i18n.on('languageChanged', storeLanguage));

// Every date-fns call without an explicit `locale` names months and weekdays in the UI language.
// The week settings are pinned to date-fns' own defaults so week math never shifts per language.
const applyDateLocale = (language: string) =>
  setDefaultOptions({ locale: dateFnsLocale(language), weekStartsOn: 0, firstWeekContainsDate: 1 });
applyDateLocale(i18n.language ?? 'en');
i18n.on('languageChanged', applyDateLocale);

export default i18n;